import type { DragEvent } from "react";
import { STATUS_LABELS, thumbPath, type ClientColor, type ItemSummary } from "@iltocat/shared";
import type { ItemView } from "../lib/useItemView.js";
import { Highlight } from "./Highlight.js";
import { Icon } from "./Icon.js";
import { Segmented } from "@vcnet/ui";

export type StockTone = "shelf" | "held" | "out" | "retired" | "none";

/** The cards-or-list switch that sits over any ItemGrid. */
export function ItemViewToggle({ value, onChange }: { value: ItemView; onChange: (next: ItemView) => void }) {
  return (
    <Segmented<ItemView>
      label="How to show items"
      size="sm"
      value={value}
      onChange={onChange}
      options={[
        { value: "cards", label: <Icon name="grid" size={16} />, title: "Cards with photos" },
        { value: "list", label: <Icon name="menu" size={16} />, title: "Compact list, no photos" },
      ]}
    />
  );
}

/**
 * The card grid, which is how an item is shown everywhere it is not being
 * edited.
 *
 * A card is a cover photo, a title and where the thing is: the three facts
 * that identify something in a basement. The thumbnail is the 400px one, which
 * is the whole reason a screen full of them feels instant, and it is lazy so
 * that a long grid does not ask for ninety images nobody has scrolled to yet.
 *
 * In a search result the card also says *why* it is there: the title with the
 * matched words marked, and one excerpt from wherever else they were found
 * (the note, the serial number, the shelf), labelled with where that was.
 * Without that, a result that matched on something invisible looks like a bug.
 *
 * `dense` is the same items as a list, a line each and no photo. A stockroom
 * is mostly things that came in on an invoice and were never photographed, so
 * a grid of them is a wall of empty squares under very long titles.
 *
 * An item with no title is not a broken card: it is the normal output of a fast
 * pass, and saying "Needs details" is both honest and a hint at what to do.
 */

export function ItemGrid({
  items,
  locationOf,
  stockOf,
  onOpen,
  dense = false,
  selection,
  onDragItem,
}: {
  /**
   * Makes each item draggable, for dropping on the location tree. The handler
   * fills in what the drag carries. The `li` is what is dragged, not the
   * button, which is not a dependable drag source in every browser.
   */
  onDragItem?: (id: string, event: DragEvent<HTMLElement>) => void;
  /**
   * Choosing mode: a click ticks the item instead of opening it, and each one
   * wears a mark. Absent, an item just opens.
   */
  selection?: { picked: ReadonlySet<string>; onToggle: (id: string) => void };
  /** One line per item and no pictures: for a list of things that mostly have none. */
  dense?: boolean;
  items: ItemSummary[];
  /**
   * In a stockroom: where the item is as stock ("On the shelf", "At Acme"),
   * so the Items screen and the Stock screen say the same thing about it.
   */
  stockOf?: (item: ItemSummary) => { label: string; tone: StockTone; color?: ClientColor | null } | null;
  /** The item's location as a path, or "" for one that is nowhere in particular. */
  locationOf: (item: ItemSummary) => string;
  onOpen: (id: string) => void;
}) {
  const drag = (id: string) =>
    onDragItem
      ? {
          draggable: true,
          className: "drag-item",
          onDragStart: (event: DragEvent<HTMLElement>) => onDragItem(id, event),
        }
      : {};

  if (dense) {
    return (
      <ul className="dense">
        {items.map((item) => {
          const where = locationOf(item);
          const stock = stockOf?.(item) ?? null;
          return (
            <li key={item.id} {...drag(item.id)}>
              <button
                type="button"
                className={selection?.picked.has(item.id) ? "dense-row dense-on" : "dense-row"}
                aria-pressed={selection ? selection.picked.has(item.id) : undefined}
                onClick={() => (selection ? selection.onToggle(item.id) : onOpen(item.id))}
              >
                {selection && (
                  <span className="dense-mark" aria-hidden="true">
                    <Icon name="check" size={14} />
                  </span>
                )}
                <span className={item.needsDetails ? "dense-title card-untitled" : "dense-title"}>
                  {item.match && item.match.title.length > 0 ? (
                    <Highlight parts={item.match.title} />
                  ) : (
                    item.title || "Needs details"
                  )}
                  {item.quantity > 1 && <span className="card-qty"> × {item.quantity}</span>}
                </span>
                <span className="dense-facts">
                  {item.status !== "keep" && (
                    <span className={`card-status-inline card-status-${item.status}`}>
                      {STATUS_LABELS[item.status]}
                    </span>
                  )}
                  {stock && (
                    <span
                      className={`card-stock card-stock-${stock.tone}`}
                      data-color={stock.color ?? undefined}
                    >
                      {stock.label}
                    </span>
                  )}
                  <span className={where ? "card-where" : "card-where card-where-none"}>
                    <Icon name="pin" size={12} />
                    <span>{where || "Not filed"}</span>
                  </span>
                </span>
                {item.match?.where && (
                  <span className="card-match dense-match">
                    <span className="card-match-where">{item.match.where}: </span>
                    <Highlight parts={item.match.excerpt} />
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ul className="grid">
      {items.map((item) => {
        const where = locationOf(item);
        const stock = stockOf?.(item) ?? null;
        return (
          <li key={item.id} {...drag(item.id)}>
            <button
              type="button"
              className={selection?.picked.has(item.id) ? "card pick-on" : "card"}
              aria-pressed={selection ? selection.picked.has(item.id) : undefined}
              onClick={() => (selection ? selection.onToggle(item.id) : onOpen(item.id))}
            >
              <span className="card-shot">
                {item.coverPhotoId ? (
                  <img
                    src={thumbPath(item.coverPhotoId)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    draggable={false}
                  />
                ) : (
                  <span className="card-noshot" aria-hidden="true">
                    <Icon name="image" size={28} />
                  </span>
                )}
                {selection && (
                  <span className="pick-mark" aria-hidden="true">
                    <Icon name="check" size={16} />
                  </span>
                )}
                {item.photoCount > 1 && (
                  <span className="card-count">
                    <Icon name="camera" size={12} />
                    {item.photoCount}
                  </span>
                )}
                {item.status !== "keep" && (
                  <span className={`card-status card-status-${item.status}`}>
                    {STATUS_LABELS[item.status]}
                  </span>
                )}
              </span>
              <span className="card-body">
                <span className={item.needsDetails ? "card-title card-untitled" : "card-title"}>
                  {item.match && item.match.title.length > 0 ? (
                    <Highlight parts={item.match.title} />
                  ) : (
                    item.title || "Needs details"
                  )}
                  {item.quantity > 1 && <span className="card-qty"> × {item.quantity}</span>}
                </span>
                <span className={where ? "card-where" : "card-where card-where-none"}>
                  <Icon name="pin" size={12} />
                  <span>{where || "Not filed"}</span>
                </span>
                {stock && (
                  <span
                    className={`card-stock card-stock-${stock.tone}`}
                    data-color={stock.color ?? undefined}
                  >
                    {stock.label}
                  </span>
                )}
                {item.match?.where && (
                  <span className="card-match">
                    <span className="card-match-where">{item.match.where}: </span>
                    <Highlight parts={item.match.excerpt} />
                  </span>
                )}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
